import {
  isActionCardWaiting,
  teammateMessageActionCardKind,
} from '@littlebird/action-cards';
import { computed, type IComputedValue } from 'mobx';

import type { ActionCard } from '@littlebird/sdk';

/** The drafts the user could send now: an empty card is a reply still to be written, which Send refuses */
export const sendableDrafts = (cards: readonly ActionCard[]): ActionCard[] =>
  cards.filter(
    (card) =>
      card.actionType === teammateMessageActionCardKind.actionType &&
      card.status === 'draft' &&
      (teammateMessageActionCardKind.decode(card.content)?.draft.trim() ?? '')
        .length > 0
  );

const GROUPED_STATUSES: ReadonlySet<ActionCard['status']> = new Set([
  'draft',
  'running',
  'completed',
  'revoked',
]);

/** The text of a message or a reply the user is sending. Null for another kind of card or an empty message. A reply with no text yet is empty text, which is still the reply to write */
const messageTextOf = (card: ActionCard): string | null => {
  if (
    card.actionType !== teammateMessageActionCardKind.actionType ||
    !GROUPED_STATUSES.has(card.status)
  )
    return null;
  const content = teammateMessageActionCardKind.decode(card.content);
  const text = content?.sent_text ?? content?.draft;
  if (text?.trim()) return text;
  return content?.incoming_text ? '' : null;
};

/** What a card's message belongs to: the assistant message that wrote it, or the card alone when it replies to a person */
export const messageOwnerOf = (card: ActionCard): string =>
  teammateMessageActionCardKind.decode(card.content)?.incoming_text
    ? card.id
    : (card.assistantMessageId ?? card.id);

/** The cards the agent wrote together for one message: the same assistant message and the same text. A reply is its own */
const buildGroups = (
  chatCards: readonly ActionCard[]
): ReadonlyMap<string, ActionCard[]> => {
  const groups = new Map<string, ActionCard[]>();
  for (const card of chatCards) {
    const text = messageTextOf(card);
    if (text === null) continue;
    const key = `${messageOwnerOf(card)}\n${text}`;
    groups.set(key, [...(groups.get(key) ?? []), card]);
  }
  const byCard = new Map<string, ActionCard[]>();
  for (const cards of groups.values()) {
    // A person the message was withdrawn from comes last
    const group = [
      ...cards.filter(({ status }) => status !== 'revoked'),
      ...cards.filter(({ status }) => status === 'revoked'),
    ];
    for (const { id } of cards) byCard.set(id, group);
  }
  return byCard;
};

// A computed per list, so the cards are decoded once however many cards ask for their group
const groupsByChatCards = new WeakMap<
  readonly ActionCard[],
  IComputedValue<ReadonlyMap<string, ActionCard[]>>
>();

/**
 * The cards that carry one outgoing message, to one person or several, whether they are still drafts or have been
 * sent: the cards of one assistant message with the same text. A reply to a message is a group of its own card. The
 * first card is one the user still has. Empty for a card of another kind or a message left empty
 */
export const messageGroupOf = (
  card: ActionCard,
  chatCards: readonly ActionCard[]
): ActionCard[] => {
  let groups = groupsByChatCards.get(chatCards);
  if (!groups) {
    groups = computed(() => buildGroups(chatCards));
    groupsByChatCards.set(chatCards, groups);
  }
  return groups.get().get(card.id) ?? [];
};

/** A card the first card of its group draws for it */
export const isMessageGroupSibling = (
  card: ActionCard,
  chatCards: readonly ActionCard[]
): boolean => {
  const group = messageGroupOf(card, chatCards);
  return group.length > 0 && group[0]?.id !== card.id;
};

/** A sent message whose answer the user has not yet marked as seen */
export const isAnswerUnseen = (card: ActionCard): boolean =>
  card.status === 'completed' &&
  card.result?.acknowledged !== true &&
  teammateMessageActionCardKind.decode(card.content)?.answered === true;

/**
 * Why the first card of a message group keeps its place in the Feed, whatever happened to that card itself: someone
 * has yet to answer ("following"), or everyone has and an answer is unseen ("unseen"). Null for any other card
 */
export const feedHoldOfGroupLead = (
  card: ActionCard,
  chatCards: readonly ActionCard[]
): 'following' | 'unseen' | null => {
  const group = messageGroupOf(card, chatCards);
  if (group[0]?.id !== card.id) return null;
  if (group.some(isActionCardWaiting)) return 'following';
  return group.some(isAnswerUnseen) ? 'unseen' : null;
};
